/**
 * 可选支付方式查表（A8 §7.8.8）—— 支付方式的结算通道、展示名、分组与「是否可选」一律取自该端点响应。
 *
 * 契约 `03-数据字典/70-产品配置.md` §3 反模式：admin 不另维护固定支付方式清单、`channel_of` 映射或展示名，
 * 这些都是 `system_payment_methods` 目录行的属性，经 §7.8.8 的 `channel` / `label` / `group` 下发给后台。
 *
 * 不在列表里的取值（目录已退役 / 不适用本 App 平台 / 非本 App 同步行）：
 * - 结算通道返 `null`（fail-closed），不算作「某通道已启用」—— 已退役取值 C 端不下发（§3.7.2 ③）；
 *   平台不适用的目录行在现行目录下只有 WEB App 的存量 IAP，C 端 WEB 发现路径同样不下发；
 * - 展示名退回原始取值，`isSelectable` 为 false，页面据此标「已退役 / 不可选」。
 * 列表未到达（`options === null`）时同样一律查不到，调用方须自行区分「还没拿到」与「确实不在列表」。
 */

import {
  type PaymentMethodOption,
  type PaymentMethodOptionsResponse,
  type SettlementChannel,
  isSettlementChannel,
} from '@novel-admin/shared';

export interface PaymentMethodOptionLookup {
  /** 可选列表是否已到达；`false` 时其余查询一律视为「未知」 */
  ready: boolean;
  optionOf(method: string): PaymentMethodOption | undefined;
  /** 结算通道：取 `channel`，不是 admin 已接入的结算通道或不在列表 → null */
  channelOf(method: string): SettlementChannel | null;
  /** 展示名：目录行取 `label`；同步行按 Stripe 类型键；不在列表 → 原始取值 */
  labelOf(method: string): string;
  /** 在可选列表内（可写成新成员 / 新设置行） */
  isSelectable(method: string): boolean;
}

/** 单个可选项的展示名（同步行没有目录展示名，按 `stripe_type` 展示，§7.8.8 字段表）。 */
export function paymentMethodOptionLabel(option: PaymentMethodOption): string {
  if (option.source === 'STRIPE_SYNC') {
    return `${option.stripe_type ?? option.payment_method} · Stripe 同步`;
  }
  return option.label ?? option.payment_method;
}

export function createPaymentMethodOptionLookup(
  options: PaymentMethodOptionsResponse | null,
): PaymentMethodOptionLookup {
  const byMethod = new Map(
    (options?.payment_methods ?? []).map((option) => [option.payment_method, option]),
  );
  return {
    ready: options !== null,
    optionOf: (method) => byMethod.get(method),
    channelOf: (method) => {
      const channel = byMethod.get(method)?.channel;
      return channel !== undefined && isSettlementChannel(channel) ? channel : null;
    },
    labelOf: (method) => {
      const option = byMethod.get(method);
      return option ? paymentMethodOptionLabel(option) : method;
    },
    isSelectable: (method) => byMethod.has(method),
  };
}
